import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Center, ErrorBox, Input, SelectBox, Spinner, useFetch, useToast } from '@vertesia/ui/core';
import { Env } from "@vertesia/ui/env";
import { useLocation } from "@vertesia/ui/router";
import { fetchComposableTokenFromFirebaseToken, useUserSession } from '@vertesia/ui/session';
import { useState } from 'react';
import { useUITranslation } from '../../i18n/index.js';
const LOOPBACK_HOSTS = new Set(['127.0.0.1', 'localhost']);
function parseRedirectUri(rawRedirect) {
    if (!rawRedirect) {
        return null;
    }
    let decoded;
    try {
        decoded = decodeURIComponent(rawRedirect);
    }
    catch {
        return null;
    }
    let parsed;
    try {
        parsed = new URL(decoded);
    }
    catch {
        return null;
    }
    if (parsed.protocol !== 'http:') {
        return null;
    }
    if (!parsed.port) {
        return null;
    }
    if (parsed.username || parsed.password) {
        return null;
    }
    if (!LOOPBACK_HOSTS.has(parsed.hostname)) {
        return null;
    }
    return parsed.toString();
}
function getClientInfo(location) {
    const params = new URLSearchParams(location.search);
    const redirect = parseRedirectUri(params.get('redirect_uri'));
    const code = params.get('code');
    if (!redirect || !code) {
        return null;
    }
    const profile = params.get('profile') ?? "default";
    const project = params.get('project') ?? undefined;
    const account = params.get('account') ?? undefined;
    return { redirect, code, profile, project, account };
}
export function TerminalLogin() {
    const [payload, setPayload] = useState();
    const [error, setError] = useState();
    const location = useLocation();
    const clientInfo = getClientInfo(location);
    const toast = useToast();
    const { t } = useUITranslation();
    const onAccept = async (data) => {
        if (!clientInfo)
            return;
        if (!data.profile) {
            toast({
                title: t('login.terminal.profileRequired'),
                description: t('login.terminal.profileRequiredDesc'),
                status: 'error',
                duration: 2000
            });
            return;
        }
        if (!data.account) {
            toast({
                title: t('login.terminal.accountRequired'),
                description: t('login.terminal.accountRequiredDesc'),
                status: 'error',
                duration: 2000
            });
            return;
        }
        if (!data.project) {
            toast({
                title: t('login.terminal.projectRequired'),
                description: t('login.terminal.projectRequiredDesc'),
                status: 'error',
                duration: 2000
            });
            return;
        }
        // expire in 1 day
        let payload;
        try {
            const token = await fetchComposableTokenFromFirebaseToken(data.account, data.project, 24 * 3600);
            if (token) {
                payload = {
                    ...data,
                    studio_server_url: Env.endpoints.studio,
                    zeno_server_url: Env.endpoints.zeno,
                    token,
                };
                await fetch(clientInfo.redirect, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify(payload)
                });
                setPayload(payload);
            }
            else {
                toast({
                    title: t('login.terminal.failedToGetToken'),
                    status: 'error',
                    duration: 5000
                });
            }
        }
        catch (err) {
            if (payload) {
                setError(err);
                setPayload(payload);
            }
            else {
                toast({
                    title: t('login.terminal.errorAuthorizingClient'),
                    description: err.message,
                    status: 'error',
                    duration: 5000
                });
            }
        }
    };
    const getPageContent = () => {
        if (!clientInfo) {
            return _jsx(ErrorBox, { title: t('login.terminal.invalidRequest'), children: t('login.terminal.invalidRequestDesc') });
        }
        return payload
            ? _jsx(AuthDoneScreen, { payload: payload, error: error })
            : _jsx(AuthAcceptScreen, { clientInfo: clientInfo, onAccept: onAccept });
    };
    const page = getPageContent();
    return (_jsx("div", { className: "w-full flex flex-col items-center gap-4 mt-24", children: page }));
}
function AuthAcceptScreen({ onAccept, clientInfo }) {
    const { client, user } = useUserSession();
    const { data: allProjects, error } = useFetch(() => user ? client.projects.list() : Promise.resolve([]), [user]);
    const { t } = useUITranslation();
    if (error) {
        return _jsx(ErrorBox, { title: t('login.terminal.errorLoadingProjects'), children: error.message });
    }
    const getEnvironmentName = () => {
        if (Env.isLocalDev) {
            return t('login.terminal.envLocalDev');
        }
        else if (Env.isDev) {
            return t('login.terminal.envStaging');
        }
        return t('login.terminal.envProduction');
    };
    const envName = getEnvironmentName();
    return user && allProjects ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: 'w-1/3', children: [_jsxs("div", { className: "mb-4 text-xl font-semibold text-info", children: ["Authorizing client on ", envName, " environment."] }), _jsxs("div", { className: 'mb-2 text-md text-muted-foreground', children: [_jsx("div", { children: t('login.terminal.clientWantsAuth') }), _jsxs("div", { children: ["The client app code is ", _jsx("b", { className: "text-foreground", children: clientInfo.code }), ". You can check if the code is correct in the terminal."] })] }), _jsxs("div", { className: 'mb-2 text-sm text-muted-foreground', children: [_jsx("div", { children: t('login.terminal.chooseAccountProject') }), _jsx("div", { children: t('login.terminal.profileNameNote') })] })] }), _jsx(ProfileForm, { onAccept: onAccept, allProjects: allProjects, data: clientInfo })] })) : _jsx(Spinner, { size: 'lg' });
}
function AuthDoneScreen({ payload, error }) {
    const toast = useToast();
    const { t } = useUITranslation();
    const onCopy = () => {
        if (payload) {
            navigator.clipboard.writeText(JSON.stringify(payload));
            toast({
                title: t('login.terminal.authPayloadCopied'),
                description: error ? t('login.terminal.authPayloadCopiedWithError', { error: error.message }) : t('login.terminal.authPayloadCopiedSuccess'),
                status: 'success',
                duration: 5000
            });
        }
    };
    return (_jsxs("div", { children: [error ?
                _jsx("div", { children: _jsx(ErrorBox, { title: t('login.terminal.failedToSendToken'), children: t('login.terminal.failedToSendTokenDesc', { error: error.message }) }) })
                : _jsx("div", { children: t('login.terminal.clientAuthenticated') }), _jsx(Center, { className: "mt-4", children: _jsx(Button, { variant: 'secondary', onClick: onCopy, children: t('login.terminal.copyAuthPayload') }) })] }));
}
function ProfileForm({ allProjects, data, onAccept }) {
    const { accounts, account, project } = useUserSession();
    const { t } = useUITranslation();
    const [currentData, setCurrentData] = useState(() => ({
        profile: data.profile,
        account: data.account ?? account?.id,
        project: data.project ?? project?.id,
    }));
    const onChangeProfile = (value) => {
        setCurrentData({ ...currentData, profile: value });
    };
    const onChangeAccount = (value) => {
        setCurrentData({ ...currentData, account: value.id, project: undefined });
    };
    const onChangeProject = (value) => {
        setCurrentData({ ...currentData, project: value.id });
    };
    const projects = allProjects.filter(p => p.account === currentData.account);
    return (_jsxs("div", { className: 'w-1/3', children: [_jsxs("div", { className: "mb-4 flex flex-col gap-2", children: [_jsx("span", { className: "font-semibold text-muted-foreground", children: t('login.terminal.profileName') }), _jsx(Input, { type: 'text', value: currentData.profile, onChange: onChangeProfile })] }), _jsxs("div", { className: "mb-4 flex flex-col gap-2", children: [_jsx("span", { className: "font-semibold text-muted-foreground", children: t('login.terminal.account') }), _jsx(SelectAccount, { value: currentData.account, onChange: onChangeAccount, accounts: accounts || [] })] }), _jsxs("div", { className: "mb-4 flex flex-col gap-2", children: [_jsx("span", { className: "font-semibold text-muted-foreground", children: t('login.terminal.project') }), _jsx(SelectProject, { value: currentData.project, onChange: onChangeProject, projects: projects })] }), _jsx("div", { className: 'mb-4 text-sm text-attention', children: t('login.terminal.browserPermissionNote') }), _jsx("div", { children: _jsx(Button, { size: 'xl', onClick: () => onAccept(currentData), children: t('login.terminal.authorizeClient') }) })] }));
}
function SelectAccount({ value, accounts, onChange }) {
    const { t } = useUITranslation();
    const _onChange = (value) => {
        onChange(value);
    };
    return _jsx(SelectBox, { options: accounts, value: accounts?.find(a => a.id === value), onChange: _onChange, by: "id", optionLabel: (option) => option.name, placeholder: t('login.terminal.selectAccount') });
}
function SelectProject({ value, projects, onChange }) {
    const { t } = useUITranslation();
    const _onChange = (value) => {
        onChange(value);
    };
    return (_jsx(SelectBox, { by: "id", value: projects.find(p => p.id === value), options: projects, optionLabel: (option) => option.name, placeholder: t('login.terminal.selectProject'), onChange: _onChange }));
}
//# sourceMappingURL=TerminalLogin.js.map